import type { Node } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { Editor, EditorEvents } from '@tiptap/react';
import {
  AddMarkStep,
  RemoveMarkStep,
  ReplaceAroundStep,
  ReplaceStep,
  type Step,
} from 'prosemirror-transform';
import type {
  CodeBlockAddedEvent,
  CodeBlockEditedEvent,
  CodeBlockRemovedEvent,
  FormatAddedEvent,
  FormatRemovedEvent,
  InlineFormat,
  TableAddedEvent,
  TableEditedEvent,
  TableRemovedEvent,
  TrackingEvents,
} from './tracking.ts';

/**
 * TipTapTransactionAdapter is a class that listens to Tiptap editor transactions and records events for format analytics.
 */
export class TipTapTransactionAdapter {
  private editor: Editor;

  constructor(
    editor: Editor,
    public onEvent?: (data: TrackingEvents) => void,
  ) {
    this.editor = editor;

    this.editor.on('transaction', this.trackTransaction);
  }

  private trackTransaction = ({ transaction }: EditorEvents['transaction']) => {
    const step = transaction.steps.at(0);
    if (!step) return;

    const inlineFormatEventData = this.getFormatDataFromStep(step, transaction);

    if (typeof this.onEvent === 'function' && inlineFormatEventData) {
      this.onEvent(inlineFormatEventData);
    }
  };

  private getFormatDataFromStep = (step: Step, transaction: Transaction) => {
    if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) {
      let name = 'FormatAdded';
      if (step instanceof RemoveMarkStep) name = 'FormatRemoved';

      let format = step.mark.type.name as InlineFormat;
      if (format === 'textStyle' && step.mark.attrs.color) {
        format = 'color';
      }

      return { format, name } as FormatAddedEvent | FormatRemovedEvent;
    }

    if (step instanceof ReplaceStep) {
      const codeBlockEditedEventData = this.createCodeBlockEditedEventData(
        step,
        transaction,
      );
      if (codeBlockEditedEventData) return codeBlockEditedEventData;
    }

    if (step instanceof ReplaceAroundStep) {
      const codeBlockEventData = this.createCodeBlockEventData(
        step,
        transaction.before,
      );
      if (codeBlockEventData) return codeBlockEventData;

      const textAlignEventData = this.createTextAlignEventData(step);
      if (textAlignEventData) return textAlignEventData;

      const listAddedEventData = this.createListEventData(
        step,
        transaction.before,
      );
      if (listAddedEventData) return listAddedEventData;

      const headingEventData = this.createHeadingEventData(step);
      if (headingEventData) return headingEventData;
    }
  };

  private createCodeBlockEditedEventData = (
    step: ReplaceStep,
    transaction: Transaction,
  ) => {
    const affectedNodeWasCodeBlock = transaction.doc.content.content.find(
      (node) => node.type.name === 'codeBlock',
    );

    if (affectedNodeWasCodeBlock) {
      return {
        name: 'CodeBlockEdited',
        locationInDocument: step.from,
      } as CodeBlockEditedEvent;
    }
  };

  private createCodeBlockEventData = (
    step: ReplaceAroundStep,
    beforeState: Node,
  ) => {
    const codeBlockAdded = step.slice.content.content.find(
      (node) => node.type.name === 'codeBlock',
    );

    if (codeBlockAdded) {
      return {
        name: 'CodeBlockAdded',
        locationInDocument: step.from,
      } as CodeBlockAddedEvent;
    }

    const codeBlockRemoved = beforeState.content.content.find(
      (node) => node.type.name === 'codeBlock',
    );

    if (codeBlockRemoved) {
      return {
        name: 'CodeBlockRemoved',
        locationInDocument: step.from,
      } as CodeBlockRemovedEvent;
    }

    return null;
  };

  private createListEventData = (
    step: ReplaceAroundStep,
    beforeState: Node,
  ) => {
    const { content } = step.slice.content;

    const listNodeAdded = content.find((node) =>
      ['bulletList', 'orderedList'].includes(node.type.name),
    );

    if (listNodeAdded) {
      return {
        format: listNodeAdded.type.name,
        name: 'FormatAdded',
      } as FormatAddedEvent;
    }

    // If a list format wasn't found, but it existed in the previous document state
    // we can safely deduce it was removed as a part of the current step
    const listNodeRemoved = beforeState.content.content.find((node) =>
      ['bulletList', 'orderedList'].includes(node.type.name),
    );

    if (listNodeRemoved) {
      return {
        format: listNodeRemoved.type.name,
        name: 'FormatRemoved',
      } as FormatRemovedEvent;
    }

    return null;
  };

  private createHeadingEventData = (step: ReplaceAroundStep) => {
    const nodeType = step.slice.content.content[0]?.type.name;
    const attrs = step.slice.content.content[0]?.attrs;
    const format = 'textStyle';

    if (nodeType === 'heading' && attrs.level) {
      return { format, name: 'FormatAdded' } as FormatAddedEvent;
    }

    if (nodeType === 'paragraph') {
      return { format, name: 'FormatRemoved' } as FormatRemovedEvent;
    }

    return null;
  };

  private createTextAlignEventData = (step: ReplaceAroundStep) => {
    const textAlign = step.slice.content.content[0]?.attrs?.textAlign;

    if (typeof textAlign === 'undefined' || textAlign === null) {
      return null;
    }

    let name = 'FormatAdded';
    if (textAlign === 'left' || textAlign === '') {
      name = 'FormatRemoved';
    }

    return { format: 'alignment', name } as
      | FormatAddedEvent
      | FormatRemovedEvent;
  };

  /*
   * createTableEventData may be called from the table extension events themselves once the TipTapTransactionAdapter has been instantiated
   */
  public createTableEventData = (
    payload: TableAddedEvent | TableRemovedEvent | TableEditedEvent,
  ) => {
    if (typeof this.onEvent === 'function') {
      this.onEvent(payload);
    }
  };
}
